import { ServiceModel, ILookupService, ServiceStatus, ServiceType } from '@truckdown/systems';
import context from '@truckdown/components-systems';
import { defineComponent, ref, Ref, onMounted } from 'vue';
import { INetworkPreferencesService, PreferenceType } from '@truckdown/locations';

interface IData {
    isLoading: Ref<boolean>;
    showDealersModal: Ref<boolean>;

    errorMessage: Ref<string | undefined>;
    
    savedNetworks: Ref<ServiceModel[]>;
    networks: Ref<ServiceModel[]>;
    selectedNetworks: Ref<ServiceModel[]>;
}

const getData = function () {
    return {
        isLoading: ref<boolean>(false),
        showDealersModal: ref<boolean>(false),

        errorMessage: ref<string | undefined>(),

        savedNetworks: ref<ServiceModel[]>([]),
        networks: ref<ServiceModel[]>([]),
        selectedNetworks: ref<ServiceModel[]>([])
    } as IData;
}

const getMethods = function (data: IData) {
    return {
        openDealersModal: function () {
            data.showDealersModal.value = true;
            data.selectedNetworks.value = [...data.savedNetworks.value];
        },
        closeDealersModal: function () {
            data.showDealersModal.value = false;
        },
        isSelectedNetwork: function (service: ServiceModel) {
            return data.selectedNetworks.value.some(s => s.code == service.code);
        },
        handleNetworkSelect: function (service: ServiceModel) {
            const idx = data.selectedNetworks.value.findIndex(s => s.code == service.code);
            if (idx < 0) {
                //add
                data.selectedNetworks.value.push(service);
                //sort
            } else {
                data.selectedNetworks.value.splice(idx, 1);
            }
        },
        clearNetworks: function () {
            data.selectedNetworks.value = [];
        },
        handleNetworksSave: async function () {
            await saveNetworks(data);
        }
    };
}

const saveNetworks = async function (data: IData) {
    try {
        data.isLoading.value = true;

        const service = await context.getService<INetworkPreferencesService>('INetworkPreferencesService');
        const codes = data.selectedNetworks.value.map(n => n.code);

        const res = await service.accountSetup({
            accountId: context.user?.accountId ?? '',
            codes: codes
        });

        if (res.ok) {
            data.savedNetworks.value = [...data.selectedNetworks.value];
            data.showDealersModal.value = false;
        } else {
            data.errorMessage.value = 'There was an error saving your dealer networks.';
        }
    }
    catch (err) {
        console.log(err);
    }
    finally {
        data.isLoading.value = false;
    }
}

const loadNetworks = async function (data: IData) {
    try {
        data.isLoading.value = true;

        const service = await context.getService<ILookupService>('ILookupService');
        const res = await service.getServices({
            sites: [context.system],
            status: ServiceStatus.Active,
            types: [ServiceType.DealerNetwork]
        });

        data.networks.value = res;
    }
    catch (err) {
        console.log(err);
    }
    finally {
        data.isLoading.value = false;
    }
}

const loadSelectedNetworks = async function (data: IData) {
    try {
        data.isLoading.value = true;

        const service = await context.getService<INetworkPreferencesService>('INetworkPreferencesService');
        const res = await service.findByAccount(context.user?.accountId ?? '');

        if (res.ok && res.results) {
            const codes = res.results.filter(r => r.type == PreferenceType.Preferred).map(r => r.code);
            const results = data.networks.value.filter(n => codes.some(c => c == n.code));

            data.savedNetworks.value = results;
        } else {
            console.log('Error getting selected networks', res);
        }
    }
    catch (err) {
        console.log(err);
    }
    finally {
        data.isLoading.value = false;
    }
}


export default defineComponent({
    props: {
    },
    name: "fleet-setup-favorites-setup",
    emits: [],
    components: {
    },
    setup: function (props: any, ctx: any) {
        const data = getData();
        const methods = getMethods(data);

        onMounted(async () => {
            await loadNetworks(data);
            await loadSelectedNetworks(data);
        });

        return {
            ...data,
            ...methods
        };
    }
});